"use client";

import type { FC } from "react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Bars3BottomRightIcon, XMarkIcon } from "@heroicons/react/20/solid";
import * as Dialog from "@radix-ui/react-dialog";
import { CmwLogo } from "@/components/logo";
import { cn } from "@/lib/cn";
import { routes } from "@/lib/routes";
import { HeaderActions } from "./header-actions";
import { rememberUtilityBarDismissed, UtilityBar } from "./utility-bar";

type PublicHeaderProps = {
  className?: string;
  utilityBarDismissed?: boolean;
};

const HIDE_EVENT = "public-header:hide";

/* A programmatic upward scroll would otherwise reveal the header. */
export const hidePublicHeader = () => window.dispatchEvent(new Event(HIDE_EVENT));

export const PublicHeader: FC<PublicHeaderProps> = ({ className, utilityBarDismissed: initiallyDismissed = false }) => {
  const [utilityBarDismissed, setUtilityBarDismissed] = useState(initiallyDismissed);
  const [menuOpen, setMenuOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const headerRef = useRef<HTMLElement>(null);

  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return;

    const root = document.documentElement;
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      const height = entry.target.getBoundingClientRect().height;
      root.style.setProperty("--public-header-height", `${height}px`);
      root.style.setProperty("--public-header-offset", `${hidden ? 0 : height}px`);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [hidden]);

  useEffect(() => {
    let lastY = window.scrollY;
    let ticking = false;

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const currentY = window.scrollY;
        const delta = currentY - lastY;
        if (!menuOpen) {
          if (currentY < 8) setHidden(false);
          else if (Math.abs(delta) > 4) setHidden(delta > 0);
        }
        lastY = currentY;
        ticking = false;
      });
    };

    const onHide = () => {
      lastY = window.scrollY;
      if (!menuOpen) setHidden(true);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener(HIDE_EVENT, onHide);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener(HIDE_EVENT, onHide);
    };
  }, [menuOpen]);

  return (
    <header
      ref={headerRef}
      className={cn(
        "fixed inset-x-0 top-0 z-40 flex flex-col transition-transform duration-300",
        hidden && "-translate-y-full",
        className,
      )}
    >
      {!utilityBarDismissed && (
        <UtilityBar
          onDismiss={() => {
            setUtilityBarDismissed(true);
            rememberUtilityBarDismissed();
          }}
        />
      )}

      <div className="rounded-b-[24px] border-b border-[rgba(10,14,20,0.05)] bg-white p-16 lg:px-46 lg:py-24">
        <div className="page-width flex items-center justify-between gap-24 lg:h-60">
          <a href={routes.cmwHome.url} aria-label="Centre for Muslim Wellbeing — home">
            <CmwLogo type="horizontal" className="lg:h-60" />
          </a>

          <HeaderActions variant="desktop" />

          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            aria-expanded={menuOpen}
            aria-controls="public-header-menu"
            aria-label={menuOpen ? "Close navigation" : "Open navigation"}
            className="lg:hidden"
          >
            {menuOpen ? (
              <XMarkIcon aria-hidden className="size-32 cursor-pointer text-neutral-black" />
            ) : (
              <Bars3BottomRightIcon aria-hidden className="size-32 cursor-pointer text-neutral-black" />
            )}
          </button>
        </div>
      </div>

      <Dialog.Root open={menuOpen} onOpenChange={setMenuOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-neutral-black/30 backdrop-blur-sm backdrop-saturate-150 data-[state=closed]:animate-[fade-out_200ms_ease-in] data-[state=open]:animate-[fade-in_200ms_ease-out] lg:hidden" />
          <Dialog.Content
            id="public-header-menu"
            className="fixed inset-y-0 left-0 z-50 flex w-310 max-w-[85vw] flex-col gap-24 bg-neutral-white p-16 outline-none data-[state=closed]:animate-[sidebar-slide-out_200ms_ease-in] data-[state=open]:animate-[sidebar-slide-in_200ms_ease-out] lg:hidden"
          >
            <Dialog.Title className="sr-only">Navigation</Dialog.Title>
            <Dialog.Description className="sr-only">Site navigation and account actions.</Dialog.Description>
            <a
              href={routes.cmwHome.url}
              aria-label="Centre for Muslim Wellbeing — home"
              onClick={() => setMenuOpen(false)}
            >
              <CmwLogo type="horizontal" />
            </a>
            <div className="flex flex-col gap-12">
              <HeaderActions variant="mobile" />
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </header>
  );
};
